import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLeague } from '@/contexts/LeagueContext';
import { useSport } from '@/contexts/SportContext';
import { ScoringRulesProvider } from '@/contexts/ScoringRulesContext';
import MLBBetaDraft from '@/components/mlb-beta/MLBBetaDraft';
import sabotagelogo from '@/assets/sabotage-logo.png';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';

const DraftRoom = () => {
  const { leagueId } = useParams<{ leagueId: string }>();
  const { user } = useAuth();
  const { setSport } = useSport();
  const { setActiveLeagueId, activeLeague, hasLeague } = useLeague();
  const navigate = useNavigate();

  useEffect(() => {
    setSport('mlb');
  }, [setSport]);

  useEffect(() => {
    if (leagueId) {
      setActiveLeagueId(leagueId);
    }
  }, [leagueId, setActiveLeagueId]);

  const handleClose = () => {
    // Notify parent window that draft room is closing
    try {
      const bc = new BroadcastChannel('sabotage-draft');
      bc.postMessage({ type: 'draft-room-closed', leagueId });
      bc.close();
    } catch {}

    // If opened as popup, close. Otherwise navigate back.
    if (window.opener) {
      window.close();
    } else {
      navigate('/beta');
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Please log in to access the draft room.</p>
      </div>
    );
  }

  return (
    <ScoringRulesProvider leagueId={activeLeague?.id || null}>
      <div className="h-screen bg-background flex flex-col overflow-hidden" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
        {/* Minimal Draft Header */}
        <header className="shrink-0 z-50 border-b border-border bg-background/95 backdrop-blur-xl">
          <div className="flex items-center justify-between px-4 h-12">
            <div className="flex items-center gap-3">
              <img src={sabotagelogo} alt="Sabotage Fantasy" className="h-7 w-auto" />
              <span className="text-sm font-bold font-heading text-foreground">Draft Room</span>
              {activeLeague && (
                <span className="text-xs text-muted-foreground hidden sm:inline">
                  : {activeLeague.name}
                </span>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClose}
              className="gap-1.5 text-muted-foreground hover:text-foreground"
            >
              <X className="w-4 h-4" />
              <span className="hidden sm:inline">Close</span>
            </Button>
          </div>
        </header>

        {/* Draft Content — fills remaining viewport */}
        <main className="flex-1 min-h-0 px-4 py-2">
          {hasLeague ? (
            <MLBBetaDraft isDraftRoom />
          ) : (
            <div className="flex items-center justify-center h-64 text-muted-foreground">
              Loading league...
            </div>
          )}
        </main>
      </div>
    </ScoringRulesProvider>
  );
};

export default DraftRoom;
